import React, { useEffect, useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  Gift,
  Mail,
  UserCheck,
  RefreshCw,
} from "lucide-react";
import { Button, ErrorMessage, Field } from "./components.jsx";
import { PACKAGES, extraChildren, money } from "../shared/catalog.js";
import { api } from "./api.js";
import { PaymentForm } from "./workshop-payment.jsx";

const steps = [
  "Check letters",
  "Pack basket",
  "Write replies",
  "Money & ready",
];
function currentStep(order) {
  return !order.build.lettersChecked
    ? 0
    : !order.build.basketPacked
      ? 1
      : !order.build.lettersWritten
        ? 2
        : 3;
}
export function BuildWorkshop({ admin, onUpdate, onChanged }) {
  const [order, setOrder] = useState(null),
    [step, setStep] = useState(0),
    [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  async function load() {
    setLoading(true);
    setError("");
    try {
      const r = await api("/api/admin/build");
      setOrder(r.order);
      setStep(r.order ? currentStep(r.order) : 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);
  async function claim() {
    setBusy(true);
    setError("");
    try {
      const r = await api("/api/admin/build/claim", {
        method: "POST",
        body: {},
      });
      setOrder(r.order);
      if (r.order) setStep(currentStep(r.order));
      await onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  async function update(patch, next) {
    setBusy(true);
    setError("");
    try {
      const result = await onUpdate(order, patch);
      setOrder(result);
      if (next !== undefined) setStep(next);
      return result;
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  if (loading)
    return (
      <div className="empty-state">
        <Gift />
        <p>Finding the next family’s order…</p>
      </div>
    );
  return (
    <section className="build-workshop">
      <div className="flow-topline">
        <span>One family at a time · oldest orders first</span>
        <button className="text-link" disabled={busy} onClick={load}>
          <RefreshCw size={13} /> Reload
        </button>
      </div>
      <ErrorMessage>{error}</ErrorMessage>
      {!order ? (
        <div className="empty-state">
          <CheckCircle2 size={42} />
          <h2>You’re all caught up.</h2>
          <p>No unclaimed orders are waiting to be built.</p>
        </div>
      ) : order.assignedTo !== admin.id ? (
        <div className="build-claim-card">
          <span className="eyebrow">NEXT IN THE CHRISTMAS QUEUE</span>
          <h2>{order.customer.name}</h2>
          <p>
            {order.number} · {order.customer.city}
          </p>
          <div className="claim-package-list">
            {order.items.map((i, n) => (
              <div key={n}>
                <Gift size={19} />
                <span>
                  {PACKAGES.find((p) => p.id === i.packageId).name}
                  <small>{i.children.map((c) => c.name).join(", ")}</small>
                </span>
              </div>
            ))}
          </div>
          <p className="muted">
            Claim this family’s order, then we’ll walk through the letters,
            basket, replies and payment together.
          </p>
          <Button disabled={busy} onClick={claim}>
            <UserCheck size={17} />
            {busy ? "Claiming…" : "Claim & start building"}
          </Button>
        </div>
      ) : (
        <>
          <header className="build-family">
            <div>
              <span className="eyebrow">YOU’RE MAKING MAGIC FOR</span>
              <h2>{order.customer.name}</h2>
              <p>
                {order.number} · {order.customer.city}
              </p>
            </div>
            <span className="badge badge-preparing">Claimed by you</span>
          </header>
          <nav className="build-steps" aria-label="Build steps">
            {steps.map((s, i) => (
              <button
                key={s}
                disabled={busy || i > currentStep(order)}
                className={
                  step === i ? "active" : i < currentStep(order) ? "done" : ""
                }
                onClick={() => setStep(i)}
              >
                <span>
                  {i < currentStep(order) ? <CheckCircle2 size={17} /> : i + 1}
                </span>
                {s}
              </button>
            ))}
          </nav>
          <div className="build-step-card">
            {step === 0 && (
              <>
                <Mail className="flow-symbol" />
                <h3>Have their letters arrived?</h3>
                <p>
                  Match by the family’s address and each child’s name. An order
                  number helps, too.
                </p>
                <div className="build-address">
                  {order.customer.address}
                  {order.customer.unit && `, ${order.customer.unit}`} ·{" "}
                  {order.customer.city}, ID {order.customer.zip}
                </div>
                <div className="build-children">
                  {order.items.map((item, i) =>
                    item.children.map((c, j) => (
                      <label className="build-letter-check" key={`${i}-${j}`}>
                        <input
                          type="checkbox"
                          checked={c.letterReceived}
                          disabled={busy}
                          onChange={(e) => {
                            const original = order;
                            const items = order.items.map((it, k) => ({
                              ...it,
                              children: it.children.map((ch, l) =>
                                k === i && l === j
                                  ? { ...ch, letterReceived: e.target.checked }
                                  : ch,
                              ),
                            }));
                            setOrder({ ...order, items });
                            update({
                              action: "details",
                              notes: order.notes,
                              letters: order.items.map((it, k) =>
                                it.children.map((ch, l) =>
                                  k === i && l === j
                                    ? e.target.checked
                                    : ch.letterReceived,
                                ),
                              ),
                            }).then((result) => {
                              if (!result) setOrder(original);
                            });
                          }}
                        />
                        <span>
                          <strong>{c.name}</strong>
                          <small>Age {c.age}</small>
                        </span>
                        <span>
                          {c.letterReceived
                            ? "Letter received"
                            : "No letter received"}
                        </span>
                      </label>
                    )),
                  )}
                </div>
                <p className="inline-notice">
                  No submitted letter is okay. Include a generic Santa letter
                  for the family.
                </p>
                <Button
                  disabled={busy}
                  onClick={() =>
                    update({ action: "build", lettersChecked: true }, 1)
                  }
                >
                  Letters checked · next <ArrowRight size={16} />
                </Button>
              </>
            )}
            {step === 1 && (
              <>
                <Gift className="flow-symbol" />
                <h3>Pack their Christmas surprise.</h3>
                <p>
                  Put together each package, including the extra-child items.
                </p>
                {order.items.map((item, i) => {
                  const p = PACKAGES.find((p) => p.id === item.packageId);
                  return (
                    <div className="build-package" key={i}>
                      <h4>{p.name}</h4>
                      <span>
                        {item.children.length} children
                        {p.kind !== "letter" && extraChildren(item) > 0
                          ? ` · ${extraChildren(item)} extra-child additions`
                          : ""}
                      </span>
                      <ul>
                        {p.contents.map((c) => (
                          <li key={c}>{c}</li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
                {order.customer.dietaryNotes && (
                  <p className="inline-notice">
                    Food requests: {order.customer.dietaryNotes}
                  </p>
                )}
                <Button
                  disabled={busy}
                  onClick={() =>
                    update({ action: "build", basketPacked: true }, 2)
                  }
                >
                  Basket packed · next <ArrowRight size={16} />
                </Button>
              </>
            )}
            {step === 2 && (
              <>
                <Mail className="flow-symbol" />
                <h3>A little reply from Santa.</h3>
                <p>Write the replies, then add them to the finished basket.</p>
                {order.items.flatMap((item, i) =>
                  item.children.map((c, j) => (
                    <article className="reply-card" key={`${i}-${j}`}>
                      <strong>
                        {c.name} · age {c.age}
                      </strong>
                      <span>
                        {c.letterReceived
                          ? "Reply to their submitted letter"
                          : "No submitted letter · generic family letter"}
                      </span>
                      {c.note && <p>{c.note}</p>}
                    </article>
                  )),
                )}
                <Button
                  disabled={busy}
                  onClick={() =>
                    update({ action: "build", lettersWritten: true }, 3)
                  }
                >
                  Replies written · next <ArrowRight size={16} />
                </Button>
              </>
            )}
            {step === 3 && (
              <>
                <CheckCircle2 className="flow-symbol" />
                <h3>One last check, then ready.</h3>
                <p>
                  Basket packed. Santa replies written. Confirm the money
                  received before delivery.
                </p>
                <PaymentForm
                  key={order.id}
                  order={order}
                  disabled={busy}
                  onUpdate={async (patch) => {
                    const result = await onUpdate(order, patch);
                    setOrder(result);
                    return result;
                  }}
                />
                <div className="ready-checklist">
                  <span>
                    <CheckCircle2 size={16} /> Basket packed
                  </span>
                  <span>
                    <CheckCircle2 size={16} /> Santa replies written
                  </span>
                  <span
                    className={order.received >= order.total ? "" : "still-due"}
                  >
                    {order.received >= order.total ? (
                      <CheckCircle2 size={16} />
                    ) : (
                      <span>○</span>
                    )}
                    {order.received >= order.total
                      ? "Payment covered"
                      : `${money(order.total - order.received)} still due`}
                  </span>
                </div>
                <Button
                  disabled={busy || order.received < order.total}
                  onClick={async () => {
                    const result = await update({
                      action: "status",
                      status: "ready",
                    });
                    if (result) {
                      await onChanged();
                      await load();
                    }
                  }}
                >
                  Mark ready for delivery <ArrowRight size={16} />
                </Button>
              </>
            )}
          </div>
        </>
      )}
    </section>
  );
}
